// src/utils/api.js
import axios from "axios";

const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
export const BASE_URL = isLocal
  ? `http://${window.location.hostname}:5000`
  : "https://api.propeople.cloud";

export function getMediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (/^(data:|blob:)/i.test(raw)) return raw;

  if (/^https?:/i.test(raw)) {
    try {
      const url = new URL(raw);
      const apiUrl = new URL(BASE_URL);
      if (url.host === apiUrl.host && url.pathname.startsWith("/uploads/")) {
        url.pathname = `/api/v1${url.pathname}`;
        return url.toString();
      }
    } catch {
      return raw;
    }
    return raw;
  }

  const mediaPath = raw.startsWith("/") ? raw : `/${raw}`;
  return mediaPath.startsWith("/uploads/")
    ? `${BASE_URL}/api/v1${mediaPath}`
    : `${BASE_URL}${mediaPath}`;
}

export function getEmployeeAvatarUrl(employeeId, value) {
  const id = Number(employeeId);
  if (!value || !Number.isInteger(id) || id < 1) return null;
  return `${BASE_URL}/api/v1/employees/${id}/avatar-file?v=${encodeURIComponent(String(value))}`;
}

const api = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  withCredentials: true, // ✅ send session cookie always
  timeout: 30000,
});

export async function fetchEmployeeAvatar(employeeId) {
  const id = Number(employeeId);
  if (!Number.isInteger(id) || id < 1) return null;
  const response = await api.get(`/employees/${id}/avatar-file`, {
    responseType: "blob",
  });
  const contentType = String(response.headers?.["content-type"] || "").toLowerCase();
  if (response.status === 204 || !response.data?.size || !contentType.startsWith("image/")) {
    return null;
  }
  return URL.createObjectURL(response.data);
}

const API_ACTIVITY_EVENT = "hrm-api-activity";
const MAX_GET_NETWORK_RETRIES = 4;
const activeRequests = new Map();

function publishApiActivity() {
  const slowRequests = [...activeRequests.values()].filter((request) => request.slow).length;
  window.dispatchEvent(new CustomEvent(API_ACTIVITY_EVENT, { detail: { slowRequests } }));
}

function trackRequest(config) {
  const requestId = `${Date.now()}-${Math.random()}`;
  config.__activityId = requestId;
  const request = { slow: false, timer: null };
  request.timer = window.setTimeout(() => {
    request.slow = true;
    publishApiActivity();
  }, 8000);
  activeRequests.set(requestId, request);
}

function finishRequest(config) {
  const requestId = config?.__activityId;
  const request = requestId ? activeRequests.get(requestId) : null;
  if (!request) return;
  window.clearTimeout(request.timer);
  activeRequests.delete(requestId);
  publishApiActivity();
}

const PORTAL_STORAGE_KEY = "activePortalCode";

export function getApiPortalCode() {
  return String(sessionStorage.getItem(PORTAL_STORAGE_KEY) || "").trim().toLowerCase();
}

export function setApiPortalCode(companyCode) {
  const nextCode = String(companyCode || "").trim().toLowerCase();
  const currentCode = getApiPortalCode();

  if (nextCode) {
    sessionStorage.setItem(PORTAL_STORAGE_KEY, nextCode);
  } else {
    sessionStorage.removeItem(PORTAL_STORAGE_KEY);
  }

  if (currentCode !== nextCode) {
    csrfToken = null;
    csrfPromise = null;
  }
}

// cache token in memory
let csrfToken = null;
// prevent multiple parallel token requests
let csrfPromise = null;

export function resetCsrfToken() {
  csrfToken = null;
  csrfPromise = null;
}

async function getCsrfToken() {
  if (csrfToken) return csrfToken;

  if (!csrfPromise) {
    csrfPromise = api
      .get("/csrf") // ✅ /api/v1/csrf
      .then((res) => {
        csrfToken = res?.data?.csrfToken || null;
        csrfPromise = null;
        return csrfToken;
      })
      .catch((err) => {
        csrfPromise = null;
        throw err;
      });
  }

  return csrfPromise;
}

/**
 * ✅ Backward compatible function.
 * Your old code calls initCsrf() in App/AuthContext/Login.
 * Keep it exported so nothing breaks.
 */
export async function initCsrf() {
  try {
    await getCsrfToken();
    return csrfToken;
  } catch (e) {
    console.error("initCsrf failed", e);
    return null;
  }
}

// ✅ attach token on write requests
api.interceptors.request.use(async (config) => {
  trackRequest(config);
  const method = (config.method || "get").toLowerCase();
  const needsToken = ["post", "put", "patch", "delete"].includes(method);
  const portalCode = getApiPortalCode();

  if (portalCode) {
    config.headers = config.headers || {};
    config.headers["x-portal-code"] = portalCode;
  }

  if (needsToken) {
    const token = csrfToken || (await getCsrfToken());
    if (token) {
      config.headers = config.headers || {};
      config.headers["x-csrf-token"] = token; // ✅ matches server allowedHeaders
    }
  }

  return config;
});

// ✅ if token becomes invalid, refresh once and retry
api.interceptors.response.use(
  (res) => {
    finishRequest(res.config);

    // The backend regenerates/destroys the session on login/logout. A CSRF
    // token belongs to the old session, so keeping it would make the first
    // protected write fail with 403 before the automatic retry succeeds.
    const responsePath = String(res.config?.url || "").split("?")[0];
    if (responsePath.endsWith("/auth/login") || responsePath.endsWith("/auth/logout")) {
      resetCsrfToken();
    }

    return res;
  },
  async (error) => {
    const original = error.config;
    finishRequest(original);

    const message =
      error?.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      "";

    const isCsrfError =
      error?.response?.status === 403 &&
      (String(message).toLowerCase().includes("csrf") || !error?.response?.data?.message); // Sometimes message might be missing or different

    if (isCsrfError && original && !original.__csrfRetry) {
      original.__csrfRetry = true;

      // clear & refetch token
      csrfToken = null;
      await getCsrfToken();

      original.headers = original.headers || {};
      original.headers["x-csrf-token"] = csrfToken;

      return api(original);
    }

    const method = String(original?.method || "get").toLowerCase();
    const isNetworkFailure = !error.response && error.code !== "ERR_CANCELED";
    if (isNetworkFailure && method === "get" && original && (original.__networkRetry || 0) < MAX_GET_NETWORK_RETRIES && navigator.onLine) {
      original.__networkRetry = (original.__networkRetry || 0) + 1;
      await new Promise((resolve) => window.setTimeout(resolve, 600 * original.__networkRetry));
      return api(original);
    }

    return Promise.reject(error);
  }
);

export default api;
